"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSession } from "next-auth/react";
import { CopyIcon, EditIcon, Loader2 } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";

import type { Service } from "@/types";
import { buildListItemFromService, ga4SelectItem } from "@/lib/analytics/ga4-ecommerce";
import { cn, getPreviewMedia, getThumbnail } from "@/lib/utils";
import { getListingCardImageUrl, getProxiedImageUrl } from "@/lib/utils/image-proxy";
import {
	getServiceMediaRasterSource,
	getSubsequentServiceMediaRasterSources,
} from "@/lib/utils/service-media-image";
import { formatServiceDisplayTitle, servicePublicPath } from "@/lib/utils/service-title";
import { isSafeLinkHref } from "@/lib/security/safe-href";
import { formatCurrency } from "@/lib/utils/currency";
import { useCurrency } from "@/lib/contexts/CurrencyContext";
import {
	getBasicTierPackage,
	getEnvatoTemplateLicenseAmountForCurrency,
	getServiceCardListingStartingAmount,
	storefrontPackagesForService,
} from "@/lib/logic";
import { useServiceCountdownOfferOverlay } from "@/hooks/useServiceCountdownOfferOverlay";
import { Discount } from "@/lib/constant";
import { isPremiumSubscriptionPlanMember } from "@/lib/api/plan";

import { Button } from "@/components/ui/button";
import MediaCarousel from "@/components/carousels/MediaCarousel";
import GalleryRasterImage from "@/components/media/GalleryRasterImage";
import { ServiceCardTitleClamp } from "@/components/cards/ServiceCardTitleClamp";
import { ServiceCardSubcategoryPills } from "@/components/cards/ServiceCardSubcategoryPills";
import { useToast } from "@/components/ui/use-toast";

const PAY_IN_FULL_DISCOUNT = 0.1;
const premiumMembershipCache = new Map<string, Promise<boolean>>();

type ServiceCardOfferBadgeProps = {
	offerOverlay: NonNullable<ReturnType<typeof useServiceCountdownOfferOverlay>>;
	compact?: boolean;
};

function getCachedPremiumMembership(planId: string | null | undefined, duration: Parameters<typeof isPremiumSubscriptionPlanMember>[1]) {
	const key = `${planId ?? ""}:${duration ?? ""}`;
	if (!premiumMembershipCache.has(key)) {
		premiumMembershipCache.set(key, isPremiumSubscriptionPlanMember(planId, duration));
	}
	return premiumMembershipCache.get(key)!;
}

function getServiceCardOfferLabel(
	offerOverlay: NonNullable<ReturnType<typeof useServiceCountdownOfferOverlay>>,
) {
	return offerOverlay.variant === "active"
		? (offerOverlay.label.match(/\d+%\s*OFF/i)?.[0] ?? offerOverlay.label)
		: offerOverlay.label;
}

function ServiceCardOfferBadge({ offerOverlay, compact = false }: ServiceCardOfferBadgeProps) {
	const cardLabel = getServiceCardOfferLabel(offerOverlay);

	return (
		<span
			className={cn(
				"-mr-3 inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-l-full rounded-r-none px-3 py-1 text-[10px] font-semibold uppercase leading-none tracking-[0.04em] text-white shadow-sm",
				compact && "-mr-2.5 px-2 py-0.5 text-[8px]",
				offerOverlay.variant === "ended"
					? "bg-[#c5cbd4] [text-shadow:0_1px_1px_rgba(0,0,0,0.35)] dark:bg-zinc-500"
					: "bg-[#ef3c4e]",
			)}
		>
			{cardLabel}
		</span>
	);
}

export type Props = {
	service: Service;
	favorite?: {
		isFavorite: boolean;
		isLoading: boolean;
		isUpdating: boolean;
		onClick: (id: Service) => Promise<void>;
	};
	editable?: { rootPath: string };
	copyable?: { rootPath: string; key: "id" | "slug" };
	listing?: { itemListId: string; itemListName: string; index: number };
	/** Eager-load preview image (e.g. first row in category grid) to reduce blank tiles while images fetch */
	mediaPriority?: boolean;
	/** Denser card for “Frequently bought together” and similar rails. */
	compact?: boolean;
	/** Compact rail: let drop shadow / hover lift paint outside the 14rem box. */
	compactShadowBleed?: boolean;
	/** Opens in-page preview instead of navigating to the service listing. */
	onActivate?: () => void;
};

export default function ServiceCard({
	service,
	favorite,
	editable,
	copyable,
	listing,
	mediaPriority = false,
	compact = false,
	compactShadowBleed = false,
	onActivate,
}: Props) {
	const { toast } = useToast();
	const router = useRouter();
	const userCurrency = useCurrency();
	const { data: session } = useSession();
	const [isPremiumMember, setIsPremiumMember] = useState(false);
	const offerOverlay = useServiceCountdownOfferOverlay(service);
	const displayTitle = formatServiceDisplayTitle(service.title);
	const cardOfferOverlay = useMemo(
		() =>
			offerOverlay
				? {
						...offerOverlay,
						label: getServiceCardOfferLabel(offerOverlay),
					}
				: null,
		[offerOverlay],
	);

	const href = servicePublicPath(service);
	const sessionPlanId = session?.user?.plan?.planID;
	const sessionPlanDuration = session?.user?.plan?.duration;

	useEffect(() => {
		if (!session?.user?.id || !sessionPlanId) {
			setIsPremiumMember(false);
			return;
		}

		let cancelled = false;
		void getCachedPremiumMembership(sessionPlanId, sessionPlanDuration).then((ok) => {
			if (!cancelled) setIsPremiumMember(ok);
		});
		return () => {
			cancelled = true;
		};
	}, [session?.user?.id, sessionPlanDuration, sessionPlanId]);

	const isCarouselChromeTarget = useCallback((target: EventTarget | null) => {
		return (
			target instanceof Element &&
			Boolean(target.closest("button, a[href], .media-carousel-chrome, [data-carousel-control]"))
		);
	}, []);

	const fireListingSelect = useCallback(() => {
		if (!listing) return;
		const item = buildListItemFromService(service, userCurrency, listing.index);
		ga4SelectItem({
			item_list_id: listing.itemListId,
			item_list_name: listing.itemListName,
			items: [item],
		});
	}, [listing, service, userCurrency]);

	const activateCard = useCallback(() => {
		fireListingSelect();
		onActivate?.();
	}, [fireListingSelect, onActivate]);

	/** Carousel contains buttons; wrapping it in `<Link>` is invalid HTML and clicks can still navigate. */
	const onMediaHeaderNavigate = useCallback(() => {
		if (onActivate) {
			activateCard();
			return;
		}
		fireListingSelect();
		if (isSafeLinkHref(href)) router.push(href);
	}, [activateCard, fireListingSelect, href, onActivate, router]);

	const onMediaHeaderClick = useCallback(
		(e: React.MouseEvent) => {
			if (isCarouselChromeTarget(e.target)) return;
			onMediaHeaderNavigate();
		},
		[isCarouselChromeTarget, onMediaHeaderNavigate],
	);

	const onMediaHeaderKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key !== "Enter" && e.key !== " ") return;
			e.preventDefault();
			onMediaHeaderNavigate();
		},
		[onMediaHeaderNavigate],
	);

	/** Same as basket line cards: full `service.media` list with URLs, else thumbnail-only slide. */
	const carouselMedia = useMemo(() => {
		const withUrl = (service.media ?? []).filter((m) => typeof m.url === "string" && m.url.trim().length > 0);
		if (withUrl.length > 0) return withUrl;
		const single = getPreviewMedia([], service.thumbnailImage);
		return single?.url ? [single] : [];
	}, [service.media, service.thumbnailImage]);
	const thumbUrl = getThumbnail(service.thumbnailImage);
	const mediaHeightClass = compact ? "h-[7.5rem]" : "h-52 sm:h-44";

	/** Static listing thumbnail — avoids per-card carousel JS and multi-image fetches. */
	const listingPreview = useMemo(() => {
		const imageSlides = carouselMedia.filter((m) => m.type !== "VIDEO");
		if (imageSlides.length === 0) {
			const src = thumbUrl
				? getListingCardImageUrl(thumbUrl, service.thumbnailDisplayUrl) ?? thumbUrl
				: undefined;
			return src ? { source: src, fallbacks: [] as string[] } : null;
		}
		const primary = imageSlides[0]!;
		if (!getServiceMediaRasterSource(primary) && !thumbUrl) return null;
		return {
			source: primary,
			fallbacks: getSubsequentServiceMediaRasterSources(imageSlides, 0),
		};
	}, [carouselMedia, thumbUrl, service.thumbnailDisplayUrl]);

	/** Compact rails: static image only — avoids carousel/demo iframe height blowout. */
	const compactPreview = useMemo(() => {
		if (!compact) return null;
		return listingPreview;
	}, [compact, listingPreview]);

	const mediaHeader = useMemo(() => {
		if (compact) {
			const shellClass = cn(
				"no-media-save relative block w-full shrink-0 overflow-hidden rounded-t-lg bg-muted/40",
				mediaHeightClass,
			);
			if (!compactPreview) {
				return <div className={shellClass} aria-hidden />;
			}
			const inner = (
				<GalleryRasterImage
					source={compactPreview.source}
					fallbackRasterSources={compactPreview.fallbacks}
					listingCard
					alt={displayTitle}
					loading={mediaPriority ? "eager" : "lazy"}
					className="size-full object-cover object-center"
				/>
			);
			if (onActivate) {
				return <div className={shellClass} onContextMenu={(event) => event.preventDefault()}>{inner}</div>;
			}
			return isSafeLinkHref(href) ? (
				<Link
					href={href}
					className={cn(shellClass, "cursor-pointer")}
					onClick={fireListingSelect}
					onContextMenu={(event) => event.preventDefault()}
				>
					{inner}
				</Link>
			) : (
				<div className={shellClass} onContextMenu={(event) => event.preventDefault()}>{inner}</div>
			);
		}

		if (carouselMedia.length > 0 && listingPreview) {
			const inner = (
				<GalleryRasterImage
					source={listingPreview.source}
					fallbackRasterSources={listingPreview.fallbacks}
					listingCard
					alt={displayTitle}
					loading={mediaPriority ? "eager" : "lazy"}
					className="size-full object-cover object-center"
				/>
			);
			const shellClass = cn(
				"no-media-save relative block w-full shrink-0 overflow-hidden rounded-t-lg bg-muted/40",
				mediaHeightClass,
			);
			if (onActivate) {
				return (
					<div className={shellClass} onContextMenu={(event) => event.preventDefault()}>
						{inner}
					</div>
				);
			}
			return isSafeLinkHref(href) ? (
				<div
					role="link"
					tabIndex={0}
					aria-label={`Open service: ${displayTitle || "details"}`}
					className={cn(shellClass, "cursor-pointer")}
					onClick={onMediaHeaderClick}
					onKeyDown={onMediaHeaderKeyDown}
					onContextMenu={(event) => event.preventDefault()}
				>
					{inner}
				</div>
			) : (
				<div className={shellClass} onContextMenu={(event) => event.preventDefault()}>
					{inner}
				</div>
			);
		}

		if (carouselMedia.length > 0) {
			const carousel = (
				<MediaCarousel
					media={carouselMedia}
					hideToolbar
					shareLink=""
					isFavorite={false}
					isFavoriteLoading={false}
					onFavoriteClick={() => {}}
					offerOverlay={null}
					priorityFirstImage={mediaPriority}
					denseCardMedia
					revealCarouselChromeOnHoverTouch
				/>
			);
			return isSafeLinkHref(href) ? (
				<div
					role="link"
					tabIndex={0}
					aria-label={`Open service: ${displayTitle || "details"}`}
					className={cn(
						"no-media-save relative block w-full shrink-0 cursor-pointer overflow-hidden rounded-t-lg bg-muted/40",
						mediaHeightClass,
					)}
					onClick={onMediaHeaderClick}
					onKeyDown={onMediaHeaderKeyDown}
					onContextMenu={(event) => event.preventDefault()}
				>
					{carousel}
				</div>
			) : (
				<div
					className={cn(
						"no-media-save relative w-full shrink-0 overflow-hidden rounded-t-lg bg-muted/40",
						mediaHeightClass,
					)}
					onContextMenu={(event) => event.preventDefault()}
				>
					{carousel}
				</div>
			);
		}
		if (thumbUrl) {
			const src =
				getListingCardImageUrl(thumbUrl, service.thumbnailDisplayUrl) ?? thumbUrl;
			const inner = (
				// eslint-disable-next-line @next/next/no-img-element
				<img
					src={src}
					alt={displayTitle}
					draggable={false}
					loading={mediaPriority ? "eager" : "lazy"}
					className="size-full object-cover object-center"
				/>
			);
			return isSafeLinkHref(href) ? (
				<Link
					href={href}
					className={cn(
						"no-media-save relative block w-full shrink-0 overflow-hidden rounded-t-lg bg-muted/40",
						mediaHeightClass,
					)}
					onClick={fireListingSelect}
					onContextMenu={(event) => event.preventDefault()}
				>
					{inner}
				</Link>
			) : (
				<div
					className={cn(
						"no-media-save relative block w-full shrink-0 overflow-hidden rounded-t-lg bg-muted/40",
						mediaHeightClass,
					)}
					onContextMenu={(event) => event.preventDefault()}
				>
					{inner}
				</div>
			);
		}
		return (
			<div
				className={cn("no-media-save w-full shrink-0 rounded-t-lg bg-muted/40", mediaHeightClass)}
				aria-hidden
			/>
		);
	}, [
		carouselMedia,
		thumbUrl,
		compactPreview,
		href,
		fireListingSelect,
		onActivate,
		mediaPriority,
		mediaHeightClass,
		compact,
		onMediaHeaderClick,
		onMediaHeaderKeyDown,
	]);

	// Basic tier + Envato template licence (matches basket line before add-ons).
	const startingPrice = useMemo(
		() => getServiceCardListingStartingAmount(service, userCurrency),
		[service, userCurrency],
	);
	const startingPackagePrice = useMemo(() => {
		const pkg = getBasicTierPackage(storefrontPackagesForService(service));
		const price = pkg?.amounts?.[userCurrency] ?? pkg?.price ?? 0;
		return typeof price === "number" && Number.isFinite(price) ? price : 0;
	}, [service, userCurrency]);
	const startingLicensePrice = useMemo(
		() => getEnvatoTemplateLicenseAmountForCurrency(service, userCurrency),
		[service, userCurrency],
	);
	const limitedOfferDiscountPercent =
		offerOverlay?.variant === "active" &&
		typeof service.countdownOffer?.discountPercent === "number" &&
		Number.isFinite(service.countdownOffer.discountPercent) &&
		service.countdownOffer.discountPercent > 0
			? Math.min(100, Math.max(0, Math.round(service.countdownOffer.discountPercent)))
			: undefined;
	const discountedStartingPrice = useMemo(() => {
		if (limitedOfferDiscountPercent === undefined) return undefined;
		const memberMultiplier = isPremiumMember ? 1 - Discount / 100 : 1;
		const limitedMultiplier = 1 - limitedOfferDiscountPercent / 100;
		const packageAfterDiscounts =
			Math.round(
				startingPackagePrice *
					memberMultiplier *
					limitedMultiplier *
					(1 - PAY_IN_FULL_DISCOUNT) *
					100,
			) / 100;
		return Math.round((packageAfterDiscounts + startingLicensePrice) * 100) / 100;
	}, [isPremiumMember, limitedOfferDiscountPercent, startingLicensePrice, startingPackagePrice]);

	const onCopy = () => {
		if (!copyable) return;

		const leading = copyable.key === "id" ? href : `?slug=${service.slug}`;
		const x = `${process.env.NEXT_PUBLIC_FRONTEND_URI}${copyable.rootPath}${leading}`;

		navigator.clipboard.writeText(x);
		toast({ title: "Copied to clipboard" });
	};

	const priceRow = discountedStartingPrice !== undefined && discountedStartingPrice < startingPrice ? (
		<div
			className={cn(
				"flex w-full min-w-0 items-baseline justify-end gap-2 leading-tight",
				compact ? "text-[9px]" : "text-[11px]",
			)}
		>
			<span className="min-w-0 truncate text-muted-foreground">
				Starting at:{" "}
				<span className="font-semibold text-tertiary line-through decoration-current">
					{formatCurrency(startingPrice, userCurrency)}
				</span>
			</span>
			<span
				className={cn(
					"shrink-0 font-bold text-[#ef3c4e] tabular-nums",
					compact ? "text-[9.5px]" : "text-xs",
				)}
			>
				{formatCurrency(discountedStartingPrice, userCurrency)}
			</span>
		</div>
	) : (
		<div
			className={cn(
				"text-muted-foreground leading-tight",
				compact ? "text-[9px]" : "text-[11px]",
			)}
		>
			Starting at:{" "}
			<span className="font-semibold text-tertiary">
				{formatCurrency(startingPrice, userCurrency)}
			</span>
		</div>
	);

	const onCardActivateClick = useCallback(
		(e: React.MouseEvent) => {
			if (!onActivate) return;
			if (e.target instanceof Element && e.target.closest("button, a[href]")) return;
			activateCard();
		},
		[activateCard, onActivate],
	);

	const onCardActivateKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (!onActivate) return;
			if (e.key !== "Enter" && e.key !== " ") return;
			e.preventDefault();
			activateCard();
		},
		[activateCard, onActivate],
	);

	const cardBodyClassName = compact
		? "flex min-h-0 flex-1 flex-col outline-none px-2.5 py-1.5"
		: "flex min-h-0 flex-1 flex-col outline-none px-3 py-2";

	const cardBodyContent = (
		<div className="flex min-h-0 min-w-0 flex-1 flex-col">
			<div className="flex shrink-0 flex-col gap-0.5">
				<div
					className={cn(
						"flex shrink-0 items-center gap-2 leading-tight",
						compact ? "h-3.5" : "h-[1.125rem]",
					)}
				>
					<span
						className={cn(
							"min-w-0 flex-1 truncate text-brand-primary",
							compact ? "text-[9px]" : "text-[11px]",
						)}
					>
						{service.categoryId?.title || "Uncategorized"}
					</span>
					{cardOfferOverlay ? (
						<ServiceCardOfferBadge offerOverlay={cardOfferOverlay} compact={compact} />
					) : null}
				</div>
				<div className={compact ? "text-[11px] leading-snug" : "text-sm leading-snug"}>
					<div
						className={cn(
							"flex shrink-0 flex-col",
							compact ? "h-[3.75rem] gap-0" : "h-[calc(3lh+0.25rem+2rem)] gap-1",
						)}
					>
						<ServiceCardTitleClamp
							text={displayTitle}
							className={compact ? "h-10 min-h-10 max-h-10 text-[11px] leading-[1.2]" : undefined}
							textClassName={compact ? "text-[11px] font-medium leading-[1.2]" : undefined}
							fadeClassName={compact ? "h-4" : undefined}
						/>
						<ServiceCardSubcategoryPills
							service={service}
							variant={compact ? "compactCard" : "card"}
						/>
					</div>
				</div>
			</div>

			<div
				className={cn(
					"flex shrink-0 items-center justify-end leading-tight",
					compact ? "mt-auto pb-1 pt-1" : "mt-auto mt-3 min-h-[1.125rem] pb-5",
				)}
			>
				{priceRow}
			</div>
		</div>
	);

	return (
		<div
			className={cn(
				"relative flex cursor-pointer flex-col rounded-lg bg-card shadow-custom transition-transform",
				compact
					? cn(
							"h-[15rem] w-[14rem] hover:-translate-y-0.5",
							compactShadowBleed ? "overflow-visible" : "overflow-hidden",
						)
					: "h-[22.5rem] hover:-translate-y-1 sm:h-[calc(21rem-1rem+0.5rem)]",
			)}
			role={onActivate ? "button" : undefined}
			tabIndex={onActivate ? 0 : undefined}
			aria-label={onActivate ? `Preview ${displayTitle || "service"}` : undefined}
			onClick={onActivate ? onCardActivateClick : undefined}
			onKeyDown={onActivate ? onCardActivateKeyDown : undefined}
		>
			<div className="absolute left-2 top-2 z-20 flex gap-2">
				{editable !== undefined && (
					<Button size="icon" variant="outline" className="size-8 shadow-custom" asChild>
						<Link href={`${editable.rootPath}/${service._id}`}>
							<EditIcon className="size-5" />
						</Link>
					</Button>
				)}
				{copyable !== undefined && (
					<Button
						size="icon"
						variant="outline"
						className="size-8 shadow-custom"
						onClick={onCopy}
					>
						<CopyIcon className="size-5" />
					</Button>
				)}
			</div>

			{mediaHeader}

			{favorite && (
				<div className="absolute right-2 top-2">
					<button
						type="button"
						disabled={Boolean(favorite.isLoading || favorite.isUpdating)}
						onClick={() => favorite.onClick(service)}
						className="-mt-0.5 transition-all hover:scale-110 disabled:pointer-events-none disabled:opacity-60"
					>
						{favorite.isUpdating ? (
							<Loader2 className="size-5 animate-spin text-secondary" aria-label="Updating saved services" />
						) : favorite.isLoading ? (
							<FontAwesomeIcon
								size="lg"
								icon={solidHeart}
								className="text-secondary/35"
								aria-hidden
							/>
						) : (
							<FontAwesomeIcon
								size="lg"
								icon={favorite.isFavorite ? solidHeart : solidHeart}
								className={
									favorite.isFavorite ? "text-red-500" : "text-secondary opacity-70 transition-colors hover:opacity-90"
								}
								style={
									favorite.isFavorite
										? { color: "#ef4444" }
										: { color: "rgba(7, 168, 188, 0.7)" }
								}
							/>
						)}
					</button>
				</div>
			)}

			{onActivate ? (
				<div className={cardBodyClassName}>{cardBodyContent}</div>
			) : (
				<Link href={href} className={cardBodyClassName} onClick={fireListingSelect}>
					{cardBodyContent}
				</Link>
			)}
		</div>
	);
}

export function ServiceCardSkeleton() {
	return (
		<div className="flex h-[22.5rem] flex-col rounded-lg bg-background shadow-custom sm:h-[calc(21rem-1rem+0.5rem)]">
			<div className="h-52 w-full shrink-0 animate-pulse rounded-t-lg bg-input sm:h-44" />

			<div className="flex min-h-0 flex-1 flex-col px-3 py-2">
				<div className="flex shrink-0 flex-col gap-0.5">
					<div className="h-[1.125rem] w-12 shrink-0 animate-pulse rounded-full bg-input" />
					<div className="text-sm leading-snug">
						<div className="flex h-[calc(3lh+0.25rem+2rem)] shrink-0 flex-col gap-1">
							<div className="flex min-h-[3lh] max-h-[3lh] flex-col justify-start gap-1">
								<div className="h-3 w-full max-w-[12rem] animate-pulse rounded-full bg-input" />
								<div className="h-3 w-full max-w-[10rem] animate-pulse rounded-full bg-input" />
								<div className="h-3 w-full max-w-[8rem] animate-pulse rounded-full bg-input" />
							</div>
							<div className="h-8 shrink-0" aria-hidden />
						</div>
					</div>
				</div>
				<div className="mt-3 flex min-h-[1.125rem] shrink-0 items-center justify-end pb-4 leading-tight">
					<div className="h-3 w-24 animate-pulse rounded-full bg-input" />
				</div>
				<div className="min-h-0 flex-1" aria-hidden />
			</div>
		</div>
	);
}
